import React, { useCallback, useState } from 'react';
import { MdCheckCircle, MdCancel, MdExpandMore, MdCameraAlt, MdShield } from 'react-icons/md';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import {
  ToolChipSpinner,
  ToolChipIcon,
  ToolImageHeader,
  ToolImageChevron,
  ToolImagePreviewContainer,
  ToolImageThumbnail,
  CodeChipWrapper,
  CodePreviewContainer,
  ToolHeaderStatic,
  ApprovalChipWrapper,
  ApprovalChipHeader,
  ApprovalCodeBlockWrapper,
  ApprovalCodeBlock,
  ApprovalActions,
  RejectionCommentArea,
  RejectionCommentInput,
  ToolChipSummary,
} from './Chat.styled';
import ArtifactCard from './ArtifactCard';
import SkillLoadChip from './SkillLoadChip';
import { useToolDisplayName } from './toolDisplayName';
import Button, { BUTTON_VARIANT_PRIMARY, BUTTON_VARIANT_TONAL } from '@components/1-atoms/Button/Button';
import useAuthImage from '@hooks/useAuthImage';
import type { ToolExecution } from '@hooks/useChat';

const LOAD_SKILL_TOOL = 'load_skill';

/** Tool chip with expandable screenshot preview and lightbox. */
const ImageToolChip: React.FC<{ tool: ToolExecution }> = ({ tool }) => {
  const [expanded, setExpanded] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const { url: blobUrl } = useAuthImage(tool.imageUrl);
  const getDisplayName = useToolDisplayName();
  const displayName = getDisplayName(tool.tool);
  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const openLightbox = useCallback(() => {
    setLightboxOpen(true);
  }, []);

  return (
    <CodeChipWrapper>
      <ToolImageHeader
        $status={tool.status}
        $success={tool.success}
        onClick={toggleExpanded}
        aria-expanded={expanded}
        type="button"
      >
        <ToolChipIcon $success={tool.success}>
          {tool.success ? <MdCheckCircle /> : <MdCancel />}
        </ToolChipIcon>
        <span>
          {displayName}
          {tool.summary && <ToolChipSummary>{tool.summary}</ToolChipSummary>}
        </span>
        <MdCameraAlt size={14} />
        <ToolImageChevron $expanded={expanded}>
          <MdExpandMore />
        </ToolImageChevron>
      </ToolImageHeader>

      <ToolImagePreviewContainer $expanded={expanded}>
        {blobUrl && (
          <ToolImageThumbnail
            src={blobUrl}
            alt={`Screenshot from ${displayName}`}
            onClick={openLightbox}
          />
        )}
      </ToolImagePreviewContainer>

      {blobUrl && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          slides={[{ src: blobUrl }]}
          plugins={[Zoom]}
          carousel={{ finite: true }}
          render={{ buttonPrev: () => null, buttonNext: () => null }}
        />
      )}
    </CodeChipWrapper>
  );
};

/** Format args JSON for human-readable display in the approval chip. */
const formatArgsForDisplay = (argsJson: string | undefined): string => {
  if (!argsJson) return '';
  try {
    const parsed = JSON.parse(argsJson);
    // For run_code: show the code field prominently
    if (typeof parsed.code === 'string') {
      const lang = parsed.language ? `# Language: ${parsed.language}\n\n` : '';
      return (lang + parsed.code).trim();
    }
    // For start_process: show command
    if (typeof parsed.command === 'string') {
      return parsed.command.trim();
    }
    // Fallback: pretty-print the whole args object
    return JSON.stringify(parsed, null, 2).trim();
  } catch {
    return argsJson;
  }
};

/** Chip rendered when a tool is awaiting user approval. */
const ApprovalToolChip: React.FC<{
  tool: ToolExecution;
  onApprove: (toolUseId: string) => Promise<void>;
  onReject: (toolUseId: string, comment?: string) => Promise<void>;
}> = ({ tool, onApprove, onReject }) => {
  const [busy, setBusy] = useState(false);
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const getDisplayName = useToolDisplayName();
  const displayName = getDisplayName(tool.tool);
  const code = formatArgsForDisplay(tool.approvalArgs);

  const handleApprove = useCallback(async () => {
    if (!tool.toolUseId || busy) return;
    setBusy(true);
    try {
      await onApprove(tool.toolUseId);
    } finally {
      setBusy(false);
    }
  }, [tool.toolUseId, busy, onApprove]);

  const handleRejectClick = useCallback(() => {
    if (busy) return;
    setShowRejectInput(true);
  }, [busy]);

  const handleRejectConfirm = useCallback(async () => {
    if (!tool.toolUseId || busy) return;
    setBusy(true);
    try {
      await onReject(tool.toolUseId, rejectComment.trim() || undefined);
    } finally {
      setBusy(false);
    }
  }, [tool.toolUseId, busy, onReject, rejectComment]);

  const handleRejectCancel = useCallback(() => {
    setShowRejectInput(false);
    setRejectComment('');
  }, []);

  return (
    <ApprovalChipWrapper>
      <ApprovalChipHeader>
        <MdShield size={18} />
        <span>
          Approval required · {displayName}
          {tool.summary && <ToolChipSummary>{tool.summary}</ToolChipSummary>}
        </span>
      </ApprovalChipHeader>

      {code && (
        <ApprovalCodeBlockWrapper>
          <ApprovalCodeBlock>{code}</ApprovalCodeBlock>
        </ApprovalCodeBlockWrapper>
      )}

      {showRejectInput && (
        <RejectionCommentArea>
          <RejectionCommentInput
            aria-label="Rejection reason"
            placeholder="Optional: tell the agent why you're rejecting this (e.g. 'use /tmp instead')"
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
            autoFocus
          />
        </RejectionCommentArea>
      )}

      <ApprovalActions>
        {showRejectInput ? (
          <>
            <Button variant={BUTTON_VARIANT_TONAL} onClick={handleRejectCancel} disabled={busy}>
              Cancel
            </Button>
            <Button variant={BUTTON_VARIANT_PRIMARY} onClick={handleRejectConfirm} disabled={busy}>
              Confirm reject
            </Button>
          </>
        ) : (
          <>
            <Button variant={BUTTON_VARIANT_TONAL} onClick={handleRejectClick} disabled={busy}>
              Reject
            </Button>
            <Button variant={BUTTON_VARIANT_PRIMARY} onClick={handleApprove} disabled={busy}>
              Approve
            </Button>
          </>
        )}
      </ApprovalActions>
    </ApprovalChipWrapper>
  );
};

/** Collapsible code chip shown after an approval-gate tool has completed. */
const ExecutedCodeChip: React.FC<{ tool: ToolExecution }> = ({ tool }) => {
  const [expanded, setExpanded] = useState(false);
  const getDisplayName = useToolDisplayName();
  const displayName = getDisplayName(tool.tool);
  const code = formatArgsForDisplay(tool.approvalArgs);
  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const isExecuting = tool.status === 'executing';

  return (
    <CodeChipWrapper>
      <ToolImageHeader
        $status={tool.status}
        $success={tool.success}
        onClick={toggleExpanded}
        aria-expanded={expanded}
        type="button"
      >
        {isExecuting ? (
          <ToolChipSpinner />
        ) : (
          <ToolChipIcon $success={tool.success}>
            {tool.success ? <MdCheckCircle /> : <MdCancel />}
          </ToolChipIcon>
        )}
        <span>
          {displayName}
          {tool.summary && <ToolChipSummary>{tool.summary}</ToolChipSummary>}
        </span>
        <ToolImageChevron $expanded={expanded}>
          <MdExpandMore />
        </ToolImageChevron>
      </ToolImageHeader>

      <CodePreviewContainer $expanded={expanded}>
        {code && <ApprovalCodeBlock>{code}</ApprovalCodeBlock>}
      </CodePreviewContainer>
    </CodeChipWrapper>
  );
};

const ToolChip: React.FC<{
  tool: ToolExecution;
  onApprove?: (toolUseId: string) => Promise<void>;
  onReject?: (toolUseId: string, comment?: string) => Promise<void>;
}> = ({ tool, onApprove, onReject }) => {
  const getDisplayName = useToolDisplayName();
  const displayName = getDisplayName(tool.tool);

  // A successful load is setup, not work. Failures fall through to the error card.
  if (tool.tool === LOAD_SKILL_TOOL && tool.success !== false) {
    return <SkillLoadChip skill={tool.summary} executing={tool.status !== 'complete'} />;
  }

  if (tool.artifactInfo) {
    return <ArtifactCard info={tool.artifactInfo} />;
  }

  // Pending approval — show code block with approve/reject buttons
  if (tool.status === 'pending_approval' && onApprove && onReject) {
    return <ApprovalToolChip tool={tool} onApprove={onApprove} onReject={onReject} />;
  }

  // Show expandable image chip for completed tools with screenshots
  if (tool.imageUrl && tool.status === 'complete' && tool.success) {
    return <ImageToolChip tool={tool} />;
  }

  // Show collapsible code preview for executing or completed approval-gate tools
  if (tool.approvalArgs && (tool.status === 'executing' || tool.status === 'complete')) {
    return <ExecutedCodeChip tool={tool} />;
  }

  return (
    <CodeChipWrapper>
      <ToolHeaderStatic
        $status={tool.status === 'pending_approval' ? 'executing' : tool.status}
        $success={tool.success}
      >
        {tool.status === 'executing' || tool.status === 'pending_approval' ? (
          <ToolChipSpinner />
        ) : (
          <ToolChipIcon $success={tool.success}>
            {tool.success ? <MdCheckCircle /> : <MdCancel />}
          </ToolChipIcon>
        )}
        <span>
          {displayName}
          {tool.summary && <ToolChipSummary>{tool.summary}</ToolChipSummary>}
        </span>
      </ToolHeaderStatic>
    </CodeChipWrapper>
  );
};

export default ToolChip;
